import { useLazyQuery, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Input, Row, Spinner } from 'reactstrap';
import GoBack from '../../../components/GoBack';
import Loader from '../../../components/layout/Loader';
import NoDataFound from '../../../components/layout/NoDataFound';
import TableComponent from '../../../components/layout/TableComponent';
import { formatDateUS, formatTime } from '../../../utils/commonFn';
import { ADMIN_ROUTE } from '../../../utils/constant';
import {
  COMPARE_RESUME_PARSERS,
  GET_CANDIDATE_RESUMES_FOR_COMPARE,
  SEARCH_CANDIDATES_FOR_COMPARE,
} from './gql';
import './style.scss';

interface CandidateEmailLite {
  id: string;
  email?: string | null;
  isPrimary?: boolean | null;
}

interface CandidateLite {
  id: string;
  fullName?: string | null;
  city?: string | null;
  stateCode?: string | null;
  emails?: CandidateEmailLite[] | null;
}

interface SearchCandidatesData {
  allCandidates: {
    candidates?: CandidateLite[] | null;
    count?: number | null;
  };
}

interface CandidateResumeLite {
  id: string;
  resumeFileName?: string | null;
  fileType?: string | null;
  originalUrl?: string | null;
  attachmentType?: string | null;
  uploadedAt?: string | null;
  createdAt?: string | null;
}

interface CandidateResumesData {
  getCandidateResumes?: CandidateResumeLite[] | null;
}

interface ResumeParseFieldDiff {
  path: string;
  status: string;
  sovrenValue?: string | null;
  claudeValue?: string | null;
}

interface ResumeParseDiffSummary {
  matching: number;
  differing: number;
  missingInSovren: number;
  missingInClaude: number;
}

interface ResumeParserComparePayload {
  resumeId: string;
  resumeFileName?: string | null;
  // Normalized parser output — arbitrary JSON shape (GraphQLJSONObject scalar).
  sovren?: Record<string, unknown> | null;
  claude?: Record<string, unknown> | null;
  sovrenError?: string | null;
  claudeError?: string | null;
  sovrenDurationMs?: number | null;
  claudeDurationMs?: number | null;
  diff?: ResumeParseFieldDiff[] | null;
  summary?: ResumeParseDiffSummary | null;
}

interface CompareResumeParsersData {
  compareResumeParsers: ResumeParserComparePayload;
}

const CANDIDATE_HEADERS = ['Name', 'Primary Email', 'Location'];

const RESUME_HEADERS = ['', 'File Name', 'File Type', 'Attachment Type', 'Uploaded'];

const DIFF_HEADERS = ['Field', 'Status', 'Sovren Value', 'Claude Value'];

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

const DIFF_STATUS_LABELS: Record<string, string> = {
  match: 'Match',
  different: 'Different',
  missing_in_sovren: 'Missing in Sovren',
  missing_in_claude: 'Missing in Claude',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '--';
  return `${formatDateUS(parsed)} ${formatTime(parsed)}`;
};

const formatDuration = (ms?: number | null): string => {
  if (ms === null || ms === undefined) return '--';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const primaryEmail = (candidate: CandidateLite): string => {
  const emails = candidate?.emails ?? [];
  const primary = emails.find((item) => item?.isPrimary) ?? emails[0];
  return primary?.email ?? '--';
};

const candidateLocation = (candidate: CandidateLite): string =>
  [candidate?.city, candidate?.stateCode].filter(Boolean).join(', ') || '--';

const diffRowClass = (status: string): string => {
  if (status === 'match') return 'diff-match';
  if (status === 'different') return 'diff-different';
  return 'diff-missing';
};

const diffBadgeColor = (status: string): string => {
  if (status === 'match') return 'success';
  if (status === 'different') return 'warning';
  return 'danger';
};

const DiffValue = ({ value }: { value?: string | null }): JSX.Element => {
  if (value === null || value === undefined) {
    return <span className="text-muted">--</span>;
  }
  return (
    <span className="diff-value" title={value}>
      {value}
    </span>
  );
};

const JsonPane = ({
  title,
  durationMs,
  payload,
  errorMessage,
}: {
  title: string;
  durationMs?: number | null;
  payload?: Record<string, unknown> | null;
  errorMessage?: string | null;
}): JSX.Element => (
  <div>
    <div className="json-pane-header">
      <span>{title}</span>
      <span className="text-muted font-weight-normal">
        {formatDuration(durationMs)}
      </span>
    </div>

    {errorMessage ? (
      <div className="text-danger mb-2">{errorMessage}</div>
    ) : null}

    <pre className="json-pane">
      {payload ? JSON.stringify(payload, null, 2) : 'No output.'}
    </pre>
  </div>
);

const ResumeParserCompare = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateLite | null>(null);
  const [selectedResumeId, setSelectedResumeId] = useState<string>('');
  const [differencesOnly, setDifferencesOnly] = useState<boolean>(true);

  const [
    searchCandidates,
    { data: candidateData, loading: candidateLoading },
  ] = useLazyQuery<SearchCandidatesData>(SEARCH_CANDIDATES_FOR_COMPARE, {
    fetchPolicy: 'network-only',
    onError: (error) => toast.error(error.message),
  });

  const {
    data: resumeData,
    loading: resumeLoading,
  } = useQuery<CandidateResumesData>(GET_CANDIDATE_RESUMES_FOR_COMPARE, {
    variables: { candidateId: selectedCandidate?.id },
    skip: !selectedCandidate?.id,
    fetchPolicy: 'network-only',
    onError: (error) => toast.error(error.message),
  });

  const [
    runCompare,
    { data: compareData, loading: compareLoading },
  ] = useLazyQuery<CompareResumeParsersData>(COMPARE_RESUME_PARSERS, {
    fetchPolicy: 'no-cache',
    onError: (error) => toast.error(error.message),
  });

  useEffect(() => {
    document.title = 'Resume Parser Compare - RealREPP';
  }, []);

  // Debounced candidate lookup. `searchText` matches names server-side while
  // `columnFilters.emailSearch` matches the email column — pick by the shape of
  // what was typed so a recruiter can paste either one.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < MIN_SEARCH_LENGTH) return;

    const timer = setTimeout(() => {
      const isEmailSearch = term.includes('@');
      searchCandidates({
        variables: {
          searchText: isEmailSearch ? '' : term,
          columnFilters: isEmailSearch ? { emailSearch: term } : {},
        },
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const selectCandidate = (candidate: CandidateLite): void => {
    setSelectedCandidate(candidate);
    setSelectedResumeId('');
  };

  const clearCandidate = (): void => {
    setSelectedCandidate(null);
    setSelectedResumeId('');
    setSearchTerm('');
  };

  const handleCompare = (): void => {
    if (!selectedCandidate?.id || !selectedResumeId) return;
    runCompare({
      variables: {
        candidateId: selectedCandidate.id,
        resumeId: selectedResumeId,
      },
    });
  };

  const candidates = candidateData?.allCandidates?.candidates ?? [];
  const resumes = resumeData?.getCandidateResumes ?? [];
  const compare = compareData?.compareResumeParsers;
  const summary = compare?.summary;
  const diff = compare?.diff ?? [];
  const visibleDiff = differencesOnly
    ? diff.filter((item) => item.status !== 'match')
    : diff;

  return (
    <div className="admin-tabs team-tabs resume-parser-compare">
      <GoBack url={ADMIN_ROUTE} />

      <div className="primaryHeaderSpacing primaryHeading">
        <h5 className="m-0">Resume Parser Compare</h5>
        <small className="text-muted">
          Runs Sovren and the Claude parser live against a stored resume and diffs
          the normalized output. Nothing is written to the candidate record.
        </small>
      </div>

      {/* Step 1 — candidate */}
      <div className="compare-step-card mr-3">
        <div className="compare-step-title">1. Select a candidate</div>

        {selectedCandidate ? (
          <div className="d-flex align-items-center flex-wrap">
            <span className="selected-candidate-chip mr-3">
              {selectedCandidate.fullName || 'Unnamed candidate'} —{' '}
              {primaryEmail(selectedCandidate)}
            </span>
            <Button
              type="button"
              size="sm"
              color="link"
              className="p-0"
              onClick={clearCandidate}
            >
              Change candidate
            </Button>
          </div>
        ) : (
          <>
            <Input
              type="text"
              value={searchTerm}
              placeholder="Search by name or email"
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            {searchTerm.trim().length >= MIN_SEARCH_LENGTH && (
              <div className="mt-3">
                <TableComponent tableHeader={CANDIDATE_HEADERS}>
                  <tbody>
                    {candidateLoading && (
                      <tr>
                        <td colSpan={CANDIDATE_HEADERS.length}>
                          <Loader loaderClass="sm-height " />
                        </td>
                      </tr>
                    )}

                    {!candidateLoading && !candidates.length && (
                      <tr>
                        <td colSpan={CANDIDATE_HEADERS.length}>
                          <div className="for-position-multiselect">
                            <NoDataFound text="No candidates matched that search." />
                          </div>
                        </td>
                      </tr>
                    )}

                    {!candidateLoading &&
                      candidates.map((candidate) => (
                        <tr
                          key={candidate.id}
                          className="candidate-result-row"
                          onClick={() => selectCandidate(candidate)}
                        >
                          <td>{candidate.fullName || '--'}</td>
                          <td>{primaryEmail(candidate)}</td>
                          <td>{candidateLocation(candidate)}</td>
                        </tr>
                      ))}
                  </tbody>
                </TableComponent>
              </div>
            )}
          </>
        )}
      </div>

      {/* Step 2 — resume */}
      {selectedCandidate && (
        <div className="compare-step-card mr-3">
          <div className="compare-step-title">2. Pick a resume</div>

          <TableComponent tableHeader={RESUME_HEADERS}>
            <tbody>
              {resumeLoading && (
                <tr>
                  <td colSpan={RESUME_HEADERS.length}>
                    <Loader loaderClass="sm-height " />
                  </td>
                </tr>
              )}

              {!resumeLoading && !resumes.length && (
                <tr>
                  <td colSpan={RESUME_HEADERS.length}>
                    <div className="for-position-multiselect">
                      <NoDataFound text="No resumes on file." />
                    </div>
                  </td>
                </tr>
              )}

              {!resumeLoading &&
                resumes.map((resume) => (
                  <tr
                    key={resume.id}
                    className={`candidate-result-row ${selectedResumeId === resume.id ? 'selected' : ''
                      }`}
                    onClick={() => setSelectedResumeId(resume.id)}
                  >
                    <td>
                      <Input
                        type="radio"
                        className="position-static m-0"
                        checked={selectedResumeId === resume.id}
                        onChange={() => setSelectedResumeId(resume.id)}
                      />
                    </td>
                    <td>{resume.resumeFileName || '--'}</td>
                    <td>{resume.fileType || '--'}</td>
                    <td>{resume.attachmentType || '--'}</td>
                    <td>{formatDateTime(resume.uploadedAt || resume.createdAt)}</td>
                  </tr>
                ))}
            </tbody>
          </TableComponent>
        </div>
      )}

      {/* Step 3 — compare */}
      {selectedCandidate && (
        <div className="compare-step-card mr-3">
          <div className="compare-step-title">3. Compare parsers</div>

          <div className="d-flex align-items-center flex-wrap">
            <Button
              type="button"
              className="buttonGenericStyle filledButton mr-3"
              disabled={!selectedResumeId || compareLoading}
              onClick={handleCompare}
            >
              {compareLoading ? <Spinner size="sm" /> : 'Compare Parsers'}
            </Button>

            <small className="text-muted">
              {compareLoading
                ? 'Calling Sovren and Claude live — can take up to a minute.'
                : 'This calls two live parsers; expect 10–60 seconds.'}
            </small>
          </div>
        </div>
      )}

      {/* Results */}
      {compare && !compareLoading && (
        <>
          <div className="compare-step-card mr-3">
            <div className="compare-step-title">
              Results — {compare.resumeFileName || compare.resumeId}
            </div>

            {summary ? (
              <div className="mb-3">
                <Badge color="success" className="mr-2">
                  Matching: {summary.matching}
                </Badge>
                <Badge color="warning" className="mr-2">
                  Differing: {summary.differing}
                </Badge>
                <Badge color="danger" className="mr-2">
                  Missing in Sovren: {summary.missingInSovren}
                </Badge>
                <Badge color="danger" className="mr-2">
                  Missing in Claude: {summary.missingInClaude}
                </Badge>
              </div>
            ) : (
              <div className="text-muted mb-3">
                No diff available — one of the parsers did not return output.
              </div>
            )}

            <Row>
              <Col md="6">
                <JsonPane
                  title="Sovren (normalized)"
                  durationMs={compare.sovrenDurationMs}
                  payload={compare.sovren}
                  errorMessage={
                    compare.sovrenError
                      ? `Sovren failed: ${compare.sovrenError}`
                      : null
                  }
                />
              </Col>
              <Col md="6">
                <JsonPane
                  title="Claude (normalized)"
                  durationMs={compare.claudeDurationMs}
                  payload={compare.claude}
                  errorMessage={
                    compare.claudeError
                      ? `Claude failed: ${compare.claudeError}`
                      : null
                  }
                />
              </Col>
            </Row>
          </div>

          {!!diff.length && (
            <div className="compare-step-card mr-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="compare-step-title mb-0">Field Diff</div>
                <Button
                  type="button"
                  size="sm"
                  color="link"
                  className="p-0"
                  onClick={() => setDifferencesOnly((prev) => !prev)}
                >
                  {differencesOnly
                    ? `Show all fields (${diff.length})`
                    : 'Show differences only'}
                </Button>
              </div>

              <div className="mt-2">
                <TableComponent tableHeader={DIFF_HEADERS}>
                  <tbody>
                    {!visibleDiff.length && (
                      <tr>
                        <td colSpan={DIFF_HEADERS.length}>
                          <div className="for-position-multiselect">
                            <NoDataFound text="No differences — every field matched." />
                          </div>
                        </td>
                      </tr>
                    )}

                    {visibleDiff.map((item) => (
                      <tr
                        key={item.path}
                        className={diffRowClass(item.status)}
                      >
                        <td>
                          <span className="diff-path">{item.path}</span>
                        </td>
                        <td>
                          <Badge color={diffBadgeColor(item.status)}>
                            {DIFF_STATUS_LABELS[item.status] || item.status}
                          </Badge>
                        </td>
                        <td>
                          <DiffValue value={item.sovrenValue} />
                        </td>
                        <td>
                          <DiffValue value={item.claudeValue} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TableComponent>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResumeParserCompare;
